import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const gpuMemoryHierarchyComponent: ComponentDef = {
  id: 'gpu-memory-hierarchy',
  name: 'GPU Memory Hierarchy',
  description: 'Visualize the bandwidth and capacity pyramid from registers to HBM, DRAM, and storage.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'hierarchy',
      options: [
        { label: 'Memory Hierarchy', value: 'hierarchy' },
        { label: 'Roofline Model', value: 'roofline' },
        { label: 'Kernel Examples', value: 'kernel' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();

    if (variant === 'roofline') {
      // Roofline model: classify workloads as compute-bound vs memory-bound
      g.input('workload', [seq_len, d_model]);
      g.scale('flops_per_byte', 'workload', 'FLOPs/Byte');
      g.scale('ridge_point', 'flops_per_byte', 'Ridge Point');
      // Compare against ridge point to classify
      g.split('compute_bound', 'ridge_point', [seq_len, d_model], 'Compute Bound');
      g.split('memory_bound', 'ridge_point', [seq_len, d_model], 'Memory Bound');
      g.output('out', 'compute_bound');
    } else if (variant === 'kernel') {
      // Kernel examples: vector add (memory-bound), matmul (compute-bound), attention (mixed)
      g.input('data', [seq_len, d_model]);

      // Vector add: memory-bound kernel
      g.linear('vec_add_w', 'data', [d_model, d_model]);
      g.activation('vec_add', 'vec_add_w', 'Vector Add (Mem-bound)');

      // Matmul: compute-bound kernel
      g.linear('matmul_w', 'data', [d_model, d_model]);
      g.scale('matmul_kernel', 'matmul_w', 'MatMul (Compute-bound)');

      // Attention: mixed kernel
      g.linear('attn_q', 'data', [d_model, d_model]);
      g.linear('attn_k', 'data', [d_model, d_model]);
      g.matmul('attn_qk', 'attn_q', 'attn_k', [seq_len, seq_len]);
      g.softmax('attn_soft', 'attn_qk', -1);
      g.scale('attn_kernel', 'attn_soft', 'Attention (Mixed)');

      g.output('out', 'attn_kernel');
    } else {
      // Memory Hierarchy (default): memory pyramid from registers to storage
      g.input('tensor', [seq_len, d_model]);

      g.scale('registers', 'tensor', 'Registers');
      g.scale('l1', 'registers', 'L1 Cache');
      g.scale('l2', 'l1', 'L2 Cache');
      g.cache('hbm', 'l2', [seq_len, d_model]);
      g.scale('dram', 'hbm', 'DRAM');
      g.scale('ssd', 'dram', 'SSD');
      g.output('out', 'ssd');
    }

    return g.build();
  }
};
